/** How ready is this learner for the real exam?
 *
 * The naive answer — average the scores of whatever they happened to
 * practise — is actively misleading: one lucky Lesen task would have
 * announced "50% exam ready" while the learner had never opened Hören.
 * A readiness number people act on (booking a real, expensive exam) has
 * to be earned, so we only call it readiness once there is enough
 * evidence behind it:
 *
 *   1. at least MIN_TASKS tasks practised, and
 *   2. at least one task practised in EVERY section of the exam.
 *
 * Until both hold, the same number is shown honestly as "average score so
 * far", together with exactly what is still missing. The 60 / 80 band
 * boundaries match the 60% pass line the mock exam scores against, so the
 * wording never contradicts the mock result.
 */
import type { ExamOverview, ExamSection } from "../../api/exams";

/** Enough tasks that one lucky or unlucky answer no longer dominates. */
export const READINESS_MIN_TASKS = 15;

export const PASS_MARK = 60;
export const STRONG_MARK = 80;

export type ReadinessState = "none" | "insufficient" | "ready";
export type ReadinessBand = "needsWork" | "onTrack" | "examReady";

export interface Readiness {
  state: ReadinessState;
  /** Weighted mean of best scores across practised tasks (0-100). */
  averageScore: number;
  practiced: number;
  total: number;
  /** How many more tasks before a readiness estimate is meaningful. */
  tasksToGo: number;
  /** Section names with no practice at all — these block readiness. */
  untouchedSections: string[];
  /** Only set once state === "ready". */
  band: ReadinessBand | null;
}

function sectionHasItems(section: ExamSection): boolean {
  return section.parts.some((p) => p.item_count > 0);
}

export function bandFor(score: number): ReadinessBand {
  if (score < PASS_MARK) return "needsWork";
  if (score < STRONG_MARK) return "onTrack";
  return "examReady";
}

export function computeReadiness(exam: ExamOverview): Readiness {
  const parts = exam.sections.flatMap((s) => s.parts);
  const practiced = parts.reduce((n, p) => n + p.practiced, 0);
  const total = parts.reduce((n, p) => n + p.item_count, 0);

  if (practiced === 0) {
    return {
      state: "none", averageScore: 0, practiced: 0, total,
      tasksToGo: READINESS_MIN_TASKS, untouchedSections: [], band: null,
    };
  }

  // Weight each part's average by how much of it the learner actually did,
  // so ten Lesen tasks count for more than one lucky Hören task.
  const practisedParts = parts.filter((p) => p.practiced > 0);
  const weighted = practisedParts.reduce((s, p) => s + p.avg_best_score * p.practiced, 0);
  const weights = practisedParts.reduce((s, p) => s + p.practiced, 0);
  const averageScore = Math.round(weighted / weights);

  const untouchedSections = exam.sections
    .filter((s) => sectionHasItems(s) && s.parts.every((p) => p.practiced === 0))
    .map((s) => s.name);

  const tasksToGo = Math.max(0, READINESS_MIN_TASKS - practiced);
  const ready = tasksToGo === 0 && untouchedSections.length === 0;

  return {
    state: ready ? "ready" : "insufficient",
    averageScore,
    practiced,
    total,
    tasksToGo,
    untouchedSections,
    band: ready ? bandFor(averageScore) : null,
  };
}
